// One booking (P2.4a coordinator view): its status and transitions, slots with
// their invitations (every C field shown), quotes, funding evidence and the
// confirm panel. Every transition is the coordinator's own click (H) with
// If-Match + a fresh Idempotency-Key; refusals are toasted as commerce words
// them. Nothing here moves money.
import React, { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { DataState } from '@/features/shared/components/ui/DataState';
import { usePrompt } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  type Booking, type BookingCandidate, type BookingClient, type Invitation, type Slot, type Terms, cancelBooking, confirmReplacement, createSlot,
  declineBooking, errorStatus, getBooking, sendInvitation, submitBooking, withdrawInvitation,
} from '@/services/baBookingService';
import { formatInZone, formatMinor, zonedLocalToIso } from './bookingFormat';
import { ConfirmPanel } from './ConfirmPanel';
import { FundingEvidenceForm } from './EvidenceForms';
import { InvitationForm } from './InvitationForm';
import { QuoteCard, QuoteDraftForm } from './QuoteBuilder';
import { Field, type K, StatusBadge, useRunAction } from './shared';

const TERMINAL = ['cancelled', 'declined', 'expired'];

export const BookingDetail: React.FC<{
  bookingId: string; k: K; clients: BookingClient[]; terms: Terms[]; onChanged: () => void;
}> = ({ bookingId, k, clients, terms, onChanged }) => {
  const prompt = usePrompt();
  const { busy, run } = useRunAction(k);
  const [state, setState] = useState<{ s: 'loading' } | { s: 'ok'; b: Booking } | { s: 'unavailable' } | { s: 'error'; msg: string }>({ s: 'loading' });
  const [newQuote, setNewQuote] = useState(false);
  const [newSlot, setNewSlot] = useState(false);
  const [tick, setTick] = useState(0);

  const load = useCallback(() => {
    getBooking(bookingId)
      .then((b) => { setState({ s: 'ok', b }); setTick((t) => t + 1); })
      .catch((e) => setState(errorStatus(e) === 404 ? { s: 'unavailable' }
        : { s: 'error', msg: getApiErrorMessage(e, k('errors.booking', 'Could not load this booking.')) }));
  }, [bookingId, k]);
  useEffect(() => { setState({ s: 'loading' }); load(); }, [load]);
  const changed = () => { load(); onChanged(); };

  if (state.s === 'loading') return <Card><CardContent><DataState variant="loading" /></CardContent></Card>;
  if (state.s === 'unavailable') return <Card><CardContent><DataState variant="unavailable" message={k('unavailable', 'Brand Ambassador booking isn’t available for this company.')} /></CardContent></Card>;
  if (state.s === 'error') return <Card><CardContent><DataState variant="error" message={state.msg} onRetry={load} /></CardContent></Card>;

  const b = state.b;
  const tz = b.timezone;
  const client = clients.find((c) => c.id === b.clientId);
  const activeTerms = terms.filter((t) => t.status === 'active' && t.offeringId === b.offeringId);
  const quotes = [...(b.quotes ?? [])].sort((x, y) => y.versionNo - x.versionNo);
  const latestCurrency = quotes[0]?.currency;
  const open = !TERMINAL.includes(b.status) && b.status !== 'confirmed';

  const reasonThen = async (title: string, confirmLabel: string, danger: boolean,
    fn: (reason: string, key: string) => Promise<Booking>, ok: string) => {
    const reason = await prompt({ title, label: k('booking.reason', 'Reason (kept with the booking)'), required: true, multiline: true, confirmLabel, danger });
    if (reason == null || !reason.trim()) return;
    if (await run((key) => fn(reason.trim(), key), ok)) changed();
  };

  return (
    <div className="space-y-4" data-testid="booking-detail">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>{b.eventTitle}</CardTitle>
            <StatusBadge status={b.status} k={k} />
            {b.fundingStatus === 'evidence_recorded' && <StatusBadge status="filled" k={k} label={k('funding.recorded_badge', 'Funding evidence recorded')} />}
          </div>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <dl className="grid gap-x-4 gap-y-1 md:grid-cols-2">
            <div><dt className="text-muted-foreground">{k('booking.client', 'Client')}</dt><dd className="text-foreground">{client?.legalName ?? '—'}</dd></div>
            <div><dt className="text-muted-foreground">{k('booking.venue', 'Venue')}</dt><dd className="text-foreground">{b.venue}</dd></div>
            <div><dt className="text-muted-foreground">{k('booking.boarding', 'Boarding')}</dt><dd>{formatInZone(b.boardingAt, tz)}</dd></div>
            <div><dt className="text-muted-foreground">{k('booking.departure', 'Departure')}</dt><dd>{formatInZone(b.departureAt, tz)}</dd></div>
            <div><dt className="text-muted-foreground">{k('booking.return', 'Return')}</dt><dd>{formatInZone(b.returnAt, tz)}</dd></div>
            <div><dt className="text-muted-foreground">{k('booking.timezone', 'Time zone')}</dt><dd>{tz}</dd></div>
          </dl>
          {b.statusReason && <p className="text-muted-foreground">{k('booking.status_reason', 'Reason: {{reason}}', { reason: b.statusReason })}</p>}
          <div className="flex flex-wrap gap-2">
            {b.status === 'draft' && (
              <Button size="sm" disabled={busy} onClick={async () => {
                if (await run((key) => submitBooking(b.id, { version: b.version, key }), k('booking.submitted', 'Booking request submitted.'))) changed();
              }}>{k('booking.submit', 'Submit request')}</Button>
            )}
            {['draft', 'requested', 'quoting'].includes(b.status) && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => reasonThen(
                k('booking.decline_title', 'Decline this booking'), k('booking.decline', 'Decline booking'), true,
                (reason, key) => declineBooking(b.id, reason, { version: b.version, key }), k('booking.declined', 'Booking declined.'))}>
                {k('booking.decline', 'Decline booking')}
              </Button>
            )}
            {!TERMINAL.includes(b.status) && (
              <Button size="sm" variant="outline" className="text-danger" disabled={busy} onClick={() => reasonThen(
                k('booking.cancel_title', 'Cancel this booking'), k('booking.cancel', 'Cancel booking'), true,
                (reason, key) => cancelBooking(b.id, reason, { version: b.version, key }), k('booking.cancelled', 'Booking cancelled.'))}>
                {k('booking.cancel', 'Cancel booking')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Slots and invitations */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle>{k('slots.title', 'Slots')}</CardTitle>
            {open && !newSlot && (
              <Button size="sm" variant="outline" onClick={() => setNewSlot(true)}><Plus className="w-4 h-4 mr-1" />{k('slots.add', 'Add slot')}</Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {newSlot && <SlotForm booking={b} k={k} onDone={() => { setNewSlot(false); changed(); }} onCancel={() => setNewSlot(false)} />}
          {(b.slots ?? []).length === 0 && !newSlot && <DataState variant="empty" compact title={k('slots.empty', 'No slots yet.')} />}
          {(b.slots ?? []).map((s) => (
            <SlotCard key={s.id} booking={b} slot={s} k={k} activeTerms={activeTerms}
              invitations={(b.invitations ?? []).filter((i) => i.slotId === s.id)} currency={latestCurrency} onChange={changed} />
          ))}
        </CardContent>
      </Card>

      {/* Quotes */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle>{k('quote.title', 'Quotes')}</CardTitle>
            {open && !newQuote && (
              <Button size="sm" variant="outline" onClick={() => setNewQuote(true)}><Plus className="w-4 h-4 mr-1" />{k('quote.new', 'New quote version')}</Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {newQuote && <QuoteDraftForm booking={b} k={k} activeTerms={activeTerms} onSaved={() => { setNewQuote(false); changed(); }} onCancel={() => setNewQuote(false)} />}
          {quotes.length === 0 && !newQuote && <DataState variant="empty" compact title={k('quote.empty', 'No quotes yet.')} />}
          {quotes.map((q) => <QuoteCard key={q.id} booking={b} quote={q} k={k} activeTerms={activeTerms} onChange={changed} />)}
        </CardContent>
      </Card>

      {/* Funding evidence */}
      <Card>
        <CardHeader><CardTitle>{k('funding.title', 'Funding evidence')}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {(b.fundingEvidence ?? []).length > 0 && (
            <ul className="space-y-1 text-sm" data-testid="funding-list">
              {(b.fundingEvidence ?? []).map((f) => (
                <li key={f.id} className="text-foreground">
                  {f.kind === 'purchase_order' ? k('funding.kind_po', 'Purchase order') : k('funding.kind_payment', 'Payment received (evidence)')}
                  <span className="text-muted-foreground"> · {formatInZone(f.recordedAt, tz)}{f.note ? ` · ${f.note}` : ''}</span>
                </li>
              ))}
            </ul>
          )}
          {!TERMINAL.includes(b.status) && <FundingEvidenceForm booking={b} k={k} onDone={() => changed()} />}
        </CardContent>
      </Card>

      {['quoting', 'awaiting_confirmation'].includes(b.status) && (
        <ConfirmPanel booking={b} k={k} reloadKey={tick} onConfirmed={() => changed()} />
      )}
    </div>
  );
};

const SlotForm: React.FC<{ booking: Booking; k: K; onDone: () => void; onCancel: () => void }> = ({ booking, k, onDone, onCancel }) => {
  const { busy, run } = useRunAction(k);
  const [f, setF] = useState({ role: '', skills: '', paidStart: '', paidEnd: '', arrival: '', duties: '' });
  const set = (n: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF((s) => ({ ...s, [n]: e.target.value }));
  const ready = f.role.trim() && f.paidStart && f.paidEnd && f.duties.trim();
  const tz = booking.timezone;
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    const out = await run(() => createSlot(booking.id, {
      role: f.role.trim(), duties: f.duties.trim(),
      requiredSkills: f.skills.split(',').map((x) => x.trim()).filter(Boolean),
      paidStartAt: zonedLocalToIso(f.paidStart, tz), paidEndAt: zonedLocalToIso(f.paidEnd, tz),
      ...(f.arrival ? { arrivalAt: zonedLocalToIso(f.arrival, tz) } : {}),
    }), k('slots.saved', 'Slot added.'));
    if (out) onDone();
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-md border border-border p-3" data-testid="slot-form">
      <div className="grid gap-3 md:grid-cols-2">
        <Field label={k('slots.role', 'Role')} htmlFor="slot-role"><Input id="slot-role" value={f.role} onChange={set('role')} required /></Field>
        <Field label={k('slots.skills', 'Required skills (comma-separated)')} htmlFor="slot-skills"><Input id="slot-skills" value={f.skills} onChange={set('skills')} /></Field>
        <Field label={k('slots.paid_start', 'Paid from ({{tz}})', { tz })} htmlFor="slot-ps"><Input id="slot-ps" type="datetime-local" value={f.paidStart} onChange={set('paidStart')} required /></Field>
        <Field label={k('slots.paid_end', 'Paid until ({{tz}})', { tz })} htmlFor="slot-pe"><Input id="slot-pe" type="datetime-local" value={f.paidEnd} onChange={set('paidEnd')} required /></Field>
        <Field label={k('slots.arrival', 'Arrival (optional)')} htmlFor="slot-arr"><Input id="slot-arr" type="datetime-local" value={f.arrival} onChange={set('arrival')} /></Field>
        <Field label={k('slots.duties', 'Duties')} htmlFor="slot-duties" className="md:col-span-2"><Textarea id="slot-duties" value={f.duties} onChange={set('duties')} required /></Field>
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={busy || !ready}>{k('slots.save', 'Add slot')}</Button>
        <Button type="button" variant="outline" onClick={onCancel}>{k('common.cancel', 'Cancel')}</Button>
      </div>
    </form>
  );
};

export const SlotCard: React.FC<{
  booking: Booking; slot: Slot; k: K; activeTerms: Terms[];
  invitations: Invitation[]; currency?: string; onChange: () => void;
}> = ({ booking, slot: s, k, activeTerms, invitations, currency, onChange }) => {
  const { busy, run } = useRunAction(k);
  const [form, setForm] = useState<{ existing?: Invitation } | null>(null);
  // Names from the slot's bookable list, once the invitation form has read it
  // (no portfolio-reviewer roster here).
  const [names, setNames] = useState<Record<string, string>>({});
  const remember = useCallback((cs: BookingCandidate[]) => setNames((m) => ({
    ...m, ...Object.fromEntries(cs.filter((c) => c.displayName).map((c) => [c.profileId, c.displayName])),
  })), []);
  const tz = booking.timezone;
  const current = invitations.filter((i) => i.staffingGeneration === s.staffingGeneration || i.status === 'draft');
  const history = invitations.filter((i) => !current.includes(i));
  const canInvite = (s.status === 'open' || s.status === 'replacement_pending') && !TERMINAL.includes(booking.status);

  return (
    <div className="space-y-2 rounded-md border border-border p-3" data-testid={`slot-${s.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-foreground">{s.role}</span>
        <StatusBadge status={s.status} k={k} />
        <span className="text-xs text-muted-foreground">
          {k('slots.paid', 'Paid {{start}} – {{end}}', { start: formatInZone(s.paidStartAt, tz), end: formatInZone(s.paidEndAt, tz) })}
        </span>
      </div>
      <p className="text-sm text-foreground whitespace-pre-line">{s.duties}</p>
      {s.requiredSkills.length > 0 && <p className="text-xs text-muted-foreground">{k('slots.skills_list', 'Skills: {{skills}}', { skills: s.requiredSkills.join(', ') })}</p>}
      <p className="text-xs text-muted-foreground" data-testid="training-note">{k('slots.training', 'Training: not checked in the pilot')}</p>

      {s.status === 'replacement_pending' && booking.status === 'confirmed' && (
        <Button size="sm" disabled={busy} onClick={async () => {
          if (await run((key) => confirmReplacement(s.id, { version: s.version, key }), k('slots.replacement_confirmed', 'Replacement confirmed.'))) onChange();
        }}>{k('slots.confirm_replacement', 'Confirm replacement')}</Button>
      )}

      {current.map((i) => (
        <InvitationRow key={i.id} invitation={i} booking={booking} k={k} name={names[i.profileId]} activeTerms={activeTerms}
          onEdit={() => setForm({ existing: i })} onChange={onChange} />
      ))}
      {form && (
        <InvitationForm booking={booking} slot={s} k={k} activeTerms={activeTerms} existing={form.existing} onCandidates={remember}
          defaultCurrency={currency} onSaved={() => { setForm(null); onChange(); }} onCancel={() => setForm(null)} />
      )}
      {!form && canInvite && !current.some((i) => i.status === 'draft') && (
        <Button size="sm" variant="outline" onClick={() => setForm({})}><Plus className="w-4 h-4 mr-1" />{k('invitation.new', 'New invitation')}</Button>
      )}
      {history.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground">{k('invitation.history', 'Earlier invitations ({{count}})', { count: history.length })}</summary>
          <div className="space-y-2 mt-2">
            {history.map((i) => <InvitationRow key={i.id} invitation={i} booking={booking} k={k} name={names[i.profileId]} activeTerms={activeTerms} onChange={onChange} readOnly />)}
          </div>
        </details>
      )}
    </div>
  );
};

export const InvitationRow: React.FC<{
  invitation: Invitation; booking: Booking; k: K; activeTerms: Terms[];
  /** Her display name from the slot's bookable list, when it has been read. */
  name?: string;
  onEdit?: () => void; onChange: () => void; readOnly?: boolean;
}> = ({ invitation: i, booking, k, name: known, activeTerms, onEdit, onChange, readOnly }) => {
  const prompt = usePrompt();
  const { busy, run } = useRunAction(k);
  const tz = booking.timezone;
  const name = known || k('invitation.ba_ref', 'Brand Ambassador ({{ref}})', { ref: i.profileId.slice(0, 8) });
  const termsName = i.termsVersionId
    ? (activeTerms.find((t) => t.id === i.termsVersionId)?.title ?? k('invitation.terms_set', 'Set ({{id}})', { id: i.termsVersionId.slice(0, 8) }))
    : k('invitation.terms_missing', 'Not set');
  const sendBlock = !i.termsVersionId
    ? (activeTerms.length === 0
      ? k('confirm.no_active_terms', 'Waiting for Fern’s cancellation and no-show terms')
      : k('invitation.need_terms', 'Choose the terms version before sending.'))
    : null;

  const send = async () => {
    if (await run((key) => sendInvitation(i.id, { version: i.version, key }), k('invitation.sent', 'Invitation sent.'))) onChange();
  };
  const withdraw = async () => {
    const reason = await prompt({
      title: k('invitation.withdraw_title', 'Withdraw this invitation'),
      message: k('invitation.withdraw_message', 'Her hold is released. To change terms, withdraw and send a new invitation.'),
      label: k('invitation.withdraw_reason', 'Reason'), required: true, multiline: true,
      confirmLabel: k('invitation.withdraw', 'Withdraw invitation'), danger: true,
    });
    if (reason == null || !reason.trim()) return;
    if (await run((key) => withdrawInvitation(i.id, reason.trim(), { version: i.version, key }), k('invitation.withdrawn', 'Invitation withdrawn.'))) onChange();
  };

  const row = (label: string, value: React.ReactNode) => (
    <div><dt className="text-muted-foreground">{label}</dt><dd className="text-foreground whitespace-pre-line">{value}</dd></div>
  );

  return (
    <div className="rounded-md bg-muted/50 p-3 space-y-2 text-sm" data-testid={`invitation-${i.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-foreground">{name}</span>
        <StatusBadge status={i.status} k={k} />
        {i.reservation && (
          <span className="text-xs text-muted-foreground">
            {k('invitation.reservation', 'Reservation: {{state}} {{start}} – {{end}}', {
              state: k(`reservation.${i.reservation.state}`, i.reservation.state),
              start: formatInZone(i.reservation.occupiedStart, tz), end: formatInZone(i.reservation.occupiedEnd, tz),
            })}
          </span>
        )}
      </div>
      <dl className="grid gap-x-4 gap-y-1 md:grid-cols-2">
        {row(k('invitation.fee', 'Flat fee for this shift'), formatMinor(i.feeAmountMinor, i.currency))}
        {row(k('invitation.currency', 'Currency'), i.currency)}
        {row(k('invitation.paid_interval', 'Paid interval'), `${formatInZone(i.paidStartAt, tz)} – ${formatInZone(i.paidEndAt, tz)}`)}
        {row(k('invitation.boarding_return', 'Boarding / return'), `${formatInZone(i.boardingAt, tz)} / ${formatInZone(i.returnAt, tz)}`)}
        {row(k('invitation.adjustment_rules', 'Adjustment rules'), i.adjustmentRules)}
        {row(k('invitation.payment_timing', 'Payment timing'), i.paymentTiming)}
        {row(k('invitation.expires', 'Invitation expires'), formatInZone(i.expiresAt, tz))}
        {row(k('invitation.hold_until', 'Hold the ambassador until'), formatInZone(i.holdUntil, tz))}
        {row(k('invitation.terms', 'Cancellation and no-show terms version'), termsName)}
      </dl>
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-2">
          {i.status === 'draft' && (
            <>
              <Button size="sm" variant="outline" disabled={busy} onClick={onEdit}>{k('invitation.edit', 'Edit draft')}</Button>
              <Button size="sm" disabled={busy || !!sendBlock} onClick={send}>{k('invitation.send', 'Send invitation')}</Button>
              {sendBlock && <span className="text-xs text-muted-foreground" data-testid="send-reason">{sendBlock}</span>}
            </>
          )}
          {(i.status === 'invited' || i.status === 'accepted') && (
            <Button size="sm" variant="outline" className="text-danger" disabled={busy} onClick={withdraw}>{k('invitation.withdraw', 'Withdraw invitation')}</Button>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingDetail;
